<template>
  <div class="gpt-logs-page">
    <div class="header-section">
      <h1 class="page-title">GPT Logs Management</h1>
      <p class="page-description">Manage GPT input/output logging data</p>
    </div>

    <!-- Search and Filter Section -->
    <v-card class="search-card mb-4">
      <v-card-title>Search & Filter</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field v-model="searchFilters.startDate" type="date" label="Start Date" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="searchFilters.endDate" type="date" label="End Date" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchFilters.searchTerm"
              label="Search by ID"
              outlined
              dense
              append-icon="mdi-magnify"
              @click:append="searchLogs"
              @keyup.enter="searchLogs"
              placeholder="Enter ID to search..."
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2">
            <v-btn color="primary" @click="searchLogs" block :loading="loading"> Search </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table -->
    <v-card>
      <v-card-title>
        GPT Logs Data
        <v-spacer></v-spacer>
        <span class="text-caption"> Total: {{ totalRecords }} records </span>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="gptLogs"
        :loading="loading"
        :server-items-length="totalRecords"
        :items-per-page="itemsPerPage"
        :page="currentPage"
        @update:page="handlePageChange"
        @update:items-per-page="handleItemsPerPageChange"
        class="elevation-1"
      >
        <template v-slot:item.input="{ item }">
          <div class="text-content">
            {{ truncateText(item.input, 100) }}
            <v-btn v-if="item.input && item.input.length > 100" icon="mdi-eye" size="small" @click="showFullText('Input', item.input)" class="ml-1"></v-btn>
          </div>
        </template>

        <template v-slot:item.output="{ item }">
          <div class="text-content">
            {{ truncateText(item.output, 100) }}
            <v-btn v-if="item.output && item.output.length > 100" icon="mdi-eye" size="small" @click="showFullText('Output', item.output)" class="ml-1"></v-btn>
          </div>
        </template>

        <template v-slot:item.timestamp="{ item }">
          {{ formatDuration(item.timestamp) }}
        </template>

        <template v-slot:item.createdDate="{ item }">
          {{ formatDateTime(item.createdDate) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog.show" max-width="800px">
      <v-card>
        <v-card-title>
          {{ dialog.isEdit ? 'Edit GPT Log' : 'Create New GPT Log' }}
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="dialog.valid">
            <v-textarea v-model="dialog.data.input" label="Input" outlined required rows="4" :rules="[(v) => !!v || 'Input is required']"></v-textarea>

            <v-textarea v-model="dialog.data.output" label="Output" outlined rows="4"></v-textarea>

            <v-text-field
              v-model="dialog.data.timestamp"
              label="Processing Time (milliseconds)"
              type="number"
              outlined
              required
              :rules="[(v) => !!v || 'Processing time is required', (v) => v > 0 || 'Processing time must be positive']"
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveRecord" :loading="dialog.loading" :disabled="!dialog.valid">
            {{ dialog.isEdit ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Details Dialog -->
    <v-dialog v-model="detailDialog.show" max-width="900px">
      <v-card>
        <v-card-title>GPT Log Details</v-card-title>

        <v-card-text v-if="detailDialog.item">
          <v-row>
            <v-col cols="12"> <strong>ID:</strong> {{ detailDialog.item.id }} </v-col>
            <v-col cols="12"> <strong>Created:</strong> {{ formatDateTime(detailDialog.item.createdDate) }} </v-col>
            <v-col cols="12"> <strong>Updated:</strong> {{ detailDialog.item.updatedDate ? formatDateTime(detailDialog.item.updatedDate) : 'Never' }} </v-col>
            <v-col cols="12"> <strong>Timestamp:</strong> {{ formatDuration(detailDialog.item.timestamp) }} </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <h3 class="mb-2">Input:</h3>
          <v-card outlined class="pa-3 mb-4" style="background-color: #f5f5f5">
            <pre class="text-wrap">{{ detailDialog.item.input }}</pre>
          </v-card>

          <h3 class="mb-2">Output:</h3>
          <v-card outlined class="pa-3" style="background-color: #f5f5f5">
            <pre class="text-wrap">{{ detailDialog.item.output || 'No output available' }}</pre>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="detailDialog.show = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Full Text Dialog -->
    <v-dialog v-model="textDialog.show" max-width="800px">
      <v-card>
        <v-card-title>{{ textDialog.title }}</v-card-title>

        <v-card-text>
          <v-card outlined class="pa-3" style="background-color: #f5f5f5">
            <pre class="text-wrap">{{ textDialog.content }}</pre>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="textDialog.show = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text> Are you sure you want to delete this GPT log record? </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="deleteDialog.show = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="deleteDialog.loading"> Delete </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'GPTLogs',
  data() {
    return {
      loading: false,
      gptLogs: [],
      totalRecords: 0,
      currentPage: 1,
      itemsPerPage: 10,
      searchFilters: {
        startDate: '',
        endDate: '',
        searchTerm: '',
        sortBy: 'createdDate',
        sortDirection: 'DESC',
      },
      headers: [
        { title: 'ID', key: 'id', width: '200px' },
        { title: 'Input', key: 'input', sortable: false, width: '250px' },
        { title: 'Output', key: 'output', sortable: false, width: '250px' },
        { title: 'Timestamp', key: 'timestamp', sortable: true, width: '150px' },
        { title: 'Created Date', key: 'createdDate', sortable: true, width: '150px' },
      ],
      dialog: {
        show: false,
        isEdit: false,
        valid: false,
        loading: false,
        data: {
          id: null,
          input: '',
          output: '',
          timestamp: 0,
        },
      },
      detailDialog: {
        show: false,
        item: null,
      },
      textDialog: {
        show: false,
        title: '',
        content: '',
      },
      deleteDialog: {
        show: false,
        loading: false,
        item: null,
      },
      snackbar: {
        show: false,
        message: '',
        color: 'success',
      },
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      this.loading = true;

      const searchRequest = {
        page: this.currentPage,
        pageSize: this.itemsPerPage,
        startDate: this.searchFilters.startDate || null,
        endDate: this.searchFilters.endDate || null,
        searchTerm: this.searchFilters.searchTerm || '',
        sortBy: this.searchFilters.sortBy,
        sortDirection: this.searchFilters.sortDirection,
      };

      this.$call.SearchGPTLogs(
        searchRequest,
        (response) => {
          this.loading = false;
          if (response.status === 0) {
            this.gptLogs = response.data.items;
            this.totalRecords = response.data.totalCount;
          } else {
            this.showSnackbar('Error loading data: ' + response.message, 'error');
          }
        },
        (error) => {
          this.loading = false;
          console.error('Error loading GPT logs:', error);
          this.showSnackbar('Error loading data', 'error');
        }
      );
    },

    searchLogs() {
      this.currentPage = 1;
      this.loadData();
    },

    refreshData() {
      this.currentPage = 1;
      this.loadData();
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.loadData();
    },

    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
      this.loadData();
    },

    openCreateDialog() {
      this.dialog = {
        show: true,
        isEdit: false,
        valid: false,
        loading: false,
        data: {
          id: null,
          input: '',
          output: '',
          timestamp: 0,
        },
      };
    },

    openEditDialog(item) {
      this.dialog = {
        show: true,
        isEdit: true,
        valid: true,
        loading: false,
        data: {
          id: item.id,
          input: item.input,
          output: item.output,
          timestamp: item.timestamp,
        },
      };
    },

    closeDialog() {
      this.dialog.show = false;
      this.$refs.form?.reset();
    },

    viewDetails(item) {
      this.detailDialog = {
        show: true,
        item: item,
      };
    },

    showFullText(title, content) {
      this.textDialog = {
        show: true,
        title: title,
        content: content,
      };
    },

    saveRecord() {
      if (!this.dialog.valid) return;

      this.dialog.loading = true;

      if (this.dialog.isEdit) {
        // Update existing record
        const updateRequest = {
          id: this.dialog.data.id,
          input: this.dialog.data.input,
          output: this.dialog.data.output,
          timestamp: parseInt(this.dialog.data.timestamp),
        };

        this.$call.UpdateGPTLog(
          updateRequest,
          (response) => {
            this.dialog.loading = false;
            if (response.status === 0) {
              this.showSnackbar('Record updated successfully', 'success');
              this.closeDialog();
              this.loadData();
            } else {
              this.showSnackbar('Error updating record: ' + response.message, 'error');
            }
          },
          (error) => {
            this.dialog.loading = false;
            console.error('Error updating record:', error);
            this.showSnackbar('Error updating record', 'error');
          }
        );
      } else {
        // Create new record
        const createRequest = {
          input: this.dialog.data.input,
          output: this.dialog.data.output,
          timestamp: this.dialog.data.timestamp,
        };

        this.$call.CreateGPTLog(
          createRequest,
          (response) => {
            this.dialog.loading = false;
            if (response.status === 0) {
              this.showSnackbar('Record created successfully', 'success');
              this.closeDialog();
              this.loadData();
            } else {
              this.showSnackbar('Error creating record: ' + response.message, 'error');
            }
          },
          (error) => {
            this.dialog.loading = false;
            console.error('Error creating record:', error);
            this.showSnackbar('Error creating record', 'error');
          }
        );
      }
    },

    openDeleteDialog(item) {
      this.deleteDialog = {
        show: true,
        loading: false,
        item: item,
      };
    },

    confirmDelete() {
      if (!this.deleteDialog.item) return;

      this.deleteDialog.loading = true;

      this.$call.DeleteGPTLog(
        this.deleteDialog.item.id,
        (response) => {
          this.deleteDialog.loading = false;
          if (response.status === 0) {
            this.showSnackbar('Record deleted successfully', 'success');
            this.deleteDialog.show = false;
            this.loadData();
          } else {
            this.showSnackbar('Error deleting record: ' + response.message, 'error');
          }
        },
        (error) => {
          this.deleteDialog.loading = false;
          console.error('Error deleting record:', error);
          this.showSnackbar('Error deleting record', 'error');
        }
      );
    },

    truncateText(text, maxLength) {
      if (!text) return '-';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString();
    },

    formatDuration(milliseconds) {
      if (milliseconds == null || milliseconds < 0) return '-';

      if (milliseconds < 1000) {
        return `${milliseconds}ms`;
      } else if (milliseconds < 60000) {
        const seconds = (milliseconds / 1000).toFixed(2);
        return `${seconds}s`;
      } else {
        const minutes = Math.floor(milliseconds / 60000);
        const seconds = ((milliseconds % 60000) / 1000).toFixed(2);
        return `${minutes}m ${seconds}s`;
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color,
      };
    },
  },
};
</script>

<style scoped>
.gpt-logs-page {
  padding: 24px;
}

.header-section {
  margin-bottom: 24px;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 8px;
}

.page-description {
  font-size: 1.1rem;
  color: #666;
  margin: 0;
}

.search-card {
  border: 1px solid #e0e0e0;
}

.actions-section {
  display: flex;
  gap: 12px;
}

.elevation-1 {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.text-caption {
  font-size: 0.875rem;
  color: #666;
}

.text-content {
  display: flex;
  align-items: center;
  max-width: 250px;
}

.text-wrap {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: monospace;
  font-size: 0.9rem;
}
</style>
